<script setup>
import { ref, onMounted, watch } from 'vue';

import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
});

const isLoading = ref(false);
const isSaving = ref(false);

const form = ref({
  ai_enabled: false,
  ai_custom_prompt: '',
  positive_expressions: '',
  negative_expressions: '',
  adversary_positive_expressions: '',
  adversary_negative_expressions: '',
  official_profiles: '',
  candidate_terms: '',
  repost_mode: 'exclusion',
  repost_deadline_hours: 24,
  exclusion_markers: '#nr',
  alert_attention_days: 3,
  alert_warning_days: 5,
  alert_critical_days: 7,
  daily_summary_time: '08:00',
  radar_volume_multiplier: 3,
  radar_min_comments: 10,
  radar_negative_pct: 30,
  whatsapp_alerts_enabled: false,
  whatsapp_staff_ids: '',
  auto_orientation_enabled: false,
  auto_orientation_classifications: '',
  auto_orientation_min_risk: 'high',
  auto_orientation_template: '',
});

function arrayToComma(arr) {
  return (arr || []).join(', ');
}

function commaToArray(str) {
  return (str || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

async function fetchConfig() {
  if (!props.selectedInbox) return;
  isLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getListeningConfig({
      inboxId: props.selectedInbox.id,
    });
    const data = response.data;
    if (data) {
      form.value = {
        ai_enabled: data.ai_enabled || false,
        ai_custom_prompt: data.ai_custom_prompt || '',
        positive_expressions: arrayToComma(data.positive_expressions),
        negative_expressions: arrayToComma(data.negative_expressions),
        adversary_positive_expressions: arrayToComma(data.adversary_positive_expressions),
        adversary_negative_expressions: arrayToComma(data.adversary_negative_expressions),
        official_profiles: arrayToComma(data.official_profiles),
        candidate_terms: arrayToComma(data.candidate_terms),
        repost_mode: data.repost_mode || 'exclusion',
        repost_deadline_hours: data.repost_deadline_hours || 24,
        exclusion_markers: arrayToComma(data.exclusion_markers) || '#nr',
        alert_attention_days: data.alert_attention_days || 3,
        alert_warning_days: data.alert_warning_days || 5,
        alert_critical_days: data.alert_critical_days || 7,
        daily_summary_time: data.daily_summary_time || '08:00',
        radar_volume_multiplier: data.radar_volume_multiplier || 3,
        radar_min_comments: data.radar_min_comments || 10,
        radar_negative_pct: data.radar_negative_pct || 30,
        whatsapp_alerts_enabled: data.whatsapp_alerts_enabled || false,
        whatsapp_staff_ids: arrayToComma(data.whatsapp_staff_ids),
        auto_orientation_enabled: data.auto_orientation_enabled || false,
        auto_orientation_classifications: arrayToComma(data.auto_orientation_classifications),
        auto_orientation_min_risk: data.auto_orientation_min_risk || 'high',
        auto_orientation_template: data.auto_orientation_template || '',
      };
    }
  } catch {
    // silently fail
  } finally {
    isLoading.value = false;
  }
}

async function saveConfig() {
  if (!props.selectedInbox) return;
  isSaving.value = true;
  try {
    const payload = {
      inboxId: props.selectedInbox.id,
      ai_enabled: form.value.ai_enabled,
      ai_custom_prompt: form.value.ai_custom_prompt.trim(),
      positive_expressions: commaToArray(form.value.positive_expressions),
      negative_expressions: commaToArray(form.value.negative_expressions),
      adversary_positive_expressions: commaToArray(form.value.adversary_positive_expressions),
      adversary_negative_expressions: commaToArray(form.value.adversary_negative_expressions),
      official_profiles: commaToArray(form.value.official_profiles),
      candidate_terms: commaToArray(form.value.candidate_terms),
      repost_mode: form.value.repost_mode,
      repost_deadline_hours: form.value.repost_deadline_hours,
      exclusion_markers: commaToArray(form.value.exclusion_markers),
      alert_attention_days: form.value.alert_attention_days,
      alert_warning_days: form.value.alert_warning_days,
      alert_critical_days: form.value.alert_critical_days,
      daily_summary_time: form.value.daily_summary_time,
      radar_volume_multiplier: form.value.radar_volume_multiplier,
      radar_min_comments: form.value.radar_min_comments,
      radar_negative_pct: form.value.radar_negative_pct,
      whatsapp_alerts_enabled: form.value.whatsapp_alerts_enabled,
      whatsapp_staff_ids: commaToArray(form.value.whatsapp_staff_ids),
      auto_orientation_enabled: form.value.auto_orientation_enabled,
      auto_orientation_classifications: commaToArray(form.value.auto_orientation_classifications),
      auto_orientation_min_risk: form.value.auto_orientation_min_risk,
      auto_orientation_template: form.value.auto_orientation_template.trim(),
    };
    await InstagramMediaReportsAPI.saveListeningConfig(payload);
  } catch {
    // silently fail
  } finally {
    isSaving.value = false;
  }
}

watch(
  () => props.selectedInbox,
  () => {
    if (props.selectedInbox) {
      fetchConfig();
    }
  }
);

onMounted(() => {
  if (props.selectedInbox) {
    fetchConfig();
  }
});
</script>

<template>
  <div>
    <div
      v-if="!selectedInbox"
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_SELECT_INBOX') }}
    </div>

    <template v-else>
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-12"
      >
        <Spinner />
      </div>

      <template v-else>
        <div class="space-y-6">
          <!-- Secao 1: Analise da ElisiA (IA) -->
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_AI_TITLE') }}
            </h3>
            <div class="space-y-4">
              <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
                <input
                  v-model="form.ai_enabled"
                  type="checkbox"
                  class="rounded border-n-weak"
                />
                {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_AI_ENABLED') }}
              </label>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_AI_CUSTOM_PROMPT') }}
                </label>
                <textarea
                  v-model="form.ai_custom_prompt"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12 resize-none"
                  rows="3"
                />
              </div>
            </div>
          </div>

          <!-- Secao 2: Posts Autorais em Posts Auditados -->
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_EXPRESSIONS_TITLE') }}
            </h3>
            <div class="space-y-4">
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_POSITIVE_EXPRESSIONS') }}
                </label>
                <input
                  v-model="form.positive_expressions"
                  type="text"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
                />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_NEGATIVE_EXPRESSIONS') }}
                </label>
                <input
                  v-model="form.negative_expressions"
                  type="text"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
                />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_ADVERSARY_POSITIVE') }}
                </label>
                <input
                  v-model="form.adversary_positive_expressions"
                  type="text"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
                />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_ADVERSARY_NEGATIVE') }}
                </label>
                <input
                  v-model="form.adversary_negative_expressions"
                  type="text"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
                />
              </div>
            </div>
          </div>

          <!-- Secao 3: Identidade da Campanha -->
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_CAMPAIGN_TITLE') }}
            </h3>
            <div class="space-y-4">
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_OFFICIAL_PROFILES') }}
                </label>
                <input
                  v-model="form.official_profiles"
                  type="text"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
                />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_CANDIDATE_TERMS') }}
                </label>
                <input
                  v-model="form.candidate_terms"
                  type="text"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
                />
              </div>
            </div>
          </div>

          <!-- Secao 4: Reposts -->
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_REPOSTS_TITLE') }}
            </h3>
            <div class="space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_REPOST_MODE') }}
                  </label>
                  <select
                    v-model="form.repost_mode"
                    class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  >
                    <option value="exclusion">
                      {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_REPOST_MODE_EXCLUSION') }}
                    </option>
                    <option value="inclusion">
                      {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_REPOST_MODE_INCLUSION') }}
                    </option>
                  </select>
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_REPOST_DEADLINE') }}
                  </label>
                  <input
                    v-model.number="form.repost_deadline_hours"
                    type="number"
                    min="1"
                    class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  />
                </div>
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_EXCLUSION_MARKERS') }}
                </label>
                <input
                  v-model="form.exclusion_markers"
                  type="text"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
                />
              </div>
            </div>
          </div>

          <!-- Secao 5: Alertas -->
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_ALERTS_TITLE') }}
            </h3>
            <div class="space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_ALERT_ATTENTION') }}
                  </label>
                  <input
                    v-model.number="form.alert_attention_days"
                    type="number"
                    min="1"
                    class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  />
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_ALERT_WARNING') }}
                  </label>
                  <input
                    v-model.number="form.alert_warning_days"
                    type="number"
                    min="1"
                    class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  />
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 mb-1 block">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_ALERT_CRITICAL') }}
                  </label>
                  <input
                    v-model.number="form.alert_critical_days"
                    type="number"
                    min="1"
                    class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  />
                </div>
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_DAILY_SUMMARY_TIME') }}
                </label>
                <input
                  v-model="form.daily_summary_time"
                  type="time"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                />
              </div>
            </div>
          </div>

          <!-- Secao 6: Criterios do Radar Completo -->
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_RADAR_TITLE') }}
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_RADAR_VOLUME') }}
                </label>
                <input
                  v-model.number="form.radar_volume_multiplier"
                  type="number"
                  min="1"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_RADAR_COMMENTS') }}
                </label>
                <input
                  v-model.number="form.radar_min_comments"
                  type="number"
                  min="0"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_RADAR_NEGATIVE_PCT') }}
                </label>
                <input
                  v-model.number="form.radar_negative_pct"
                  type="number"
                  min="0"
                  max="100"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                />
              </div>
            </div>
          </div>

          <!-- Secao 7: Alertas via WhatsApp -->
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_WHATSAPP_TITLE') }}
            </h3>
            <div class="space-y-4">
              <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
                <input
                  v-model="form.whatsapp_alerts_enabled"
                  type="checkbox"
                  class="rounded border-n-weak"
                />
                {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_WHATSAPP_ENABLED') }}
              </label>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_WHATSAPP_STAFF_IDS') }}
                </label>
                <input
                  v-model="form.whatsapp_staff_ids"
                  type="text"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
                />
              </div>
            </div>
          </div>

          <!-- Secao 8: Orientacao automatica para lideranca -->
          <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
            <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_AUTO_ORIENTATION_TITLE') }}
            </h3>
            <div class="space-y-4">
              <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
                <input
                  v-model="form.auto_orientation_enabled"
                  type="checkbox"
                  class="rounded border-n-weak"
                />
                {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_AUTO_ORIENTATION_ENABLED') }}
              </label>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_AUTO_ORIENTATION_CLASSIFICATIONS') }}
                </label>
                <input
                  v-model="form.auto_orientation_classifications"
                  type="text"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                  :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.LISTENING_COMMA_SEPARATED')"
                />
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_AUTO_ORIENTATION_MIN_RISK') }}
                </label>
                <select
                  v-model="form.auto_orientation_min_risk"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
                >
                  <option value="safe">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_RISK_SAFE') }}
                  </option>
                  <option value="medium">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_RISK_MEDIUM') }}
                  </option>
                  <option value="high">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_RISK_HIGH') }}
                  </option>
                  <option value="critical">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_RISK_CRITICAL') }}
                  </option>
                </select>
              </div>
              <div>
                <label class="text-xs text-n-slate-11 mb-1 block">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_AUTO_ORIENTATION_TEMPLATE') }}
                </label>
                <textarea
                  v-model="form.auto_orientation_template"
                  class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12 resize-none"
                  rows="4"
                  placeholder="{nome}, {perfil}"
                />
              </div>
            </div>
          </div>

          <!-- Botao Salvar -->
          <div class="flex justify-end">
            <button
              class="px-6 py-2 text-sm font-medium rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors disabled:opacity-50"
              :disabled="isSaving"
              @click="saveConfig"
            >
              <Spinner v-if="isSaving" size="small" />
              <span v-else>{{ $t('INSTAGRAM_MEDIA_REPORTS.CONFIG_SAVE_BUTTON') }}</span>
            </button>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
